/**
 * Searchable picker filter — Desk popover / phone sheet — plan/35.
 */

export type AppPickerOption = {
  value: string;
  label: string;
  /** Secondary line under the label. */
  description?: string;
  /** Extra searchable text (codes, aliases). */
  keywords?: string;
};

function normalize(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Case-insensitive match on label, description, keywords, and value. */
export function filterPickerOptions(
  options: readonly AppPickerOption[],
  query: string,
): AppPickerOption[] {
  const q = normalize(query);
  if (!q) return [...options];
  return options.filter((o) => {
    const hay = normalize(
      [o.label, o.description ?? "", o.keywords ?? "", o.value].join(" "),
    );
    return hay.includes(q);
  });
}

export function findPickerOption(
  options: readonly AppPickerOption[],
  value: string | null | undefined,
): AppPickerOption | null {
  if (value == null || value === "") return null;
  return options.find((o) => o.value === value) ?? null;
}

/** Rows the user can highlight with ↑/↓ — clear row (optional) then filtered options. */
export type PickerSelectable =
  | { kind: "clear" }
  | { kind: "option"; value: string };

export function pickerSelectables(
  filtered: readonly AppPickerOption[],
  clearLabel?: string | null,
): PickerSelectable[] {
  const rows: PickerSelectable[] = [];
  if (clearLabel) rows.push({ kind: "clear" });
  for (const o of filtered) rows.push({ kind: "option", value: o.value });
  return rows;
}

export function clampPickerActiveIndex(
  index: number,
  length: number,
): number {
  if (length <= 0) return 0;
  return Math.max(0, Math.min(index, length - 1));
}

export function stepPickerActiveIndex(
  index: number,
  length: number,
  delta: 1 | -1,
): number {
  if (length <= 0) return 0;
  return clampPickerActiveIndex(index + delta, length);
}
